// @flow
import { createSelector } from 'reselect';

import { selectRawDiscounts, selectCartReferer } from '@gandi/cart';

import { selectPersonalId } from '@gandi/redux-sharing';

import { selectWelcomeOfferEnabled } from 'src/modules/app/selectors';

import type { FullState } from '../../../types';
import type { DiscountState, Discount } from '../../types';

import { DEFAULT_BUNDLE_TYPE } from './bundle';
import { selectBundledResults } from './results';

const EMPTY_ARRAY: Array<string> = [];
const EMPTY_OBJECT = {};

const selectRaw = (state: FullState): DiscountState =>
  state.domainSuggest?.discount || undefined;

export const selectAllWelcomeOfferCode: (state: FullState) => Array<string> =
  createSelector([selectRaw, selectWelcomeOfferEnabled], (raw, offer) =>
    offer ? raw.codes.map(discount => discount.code) : EMPTY_ARRAY,
  );

type WelcomeOfferProps = {
  extension: string,
  currency: string,
};

const selectWelcomeOffer = (
  state: FullState,
  props: WelcomeOfferProps,
): ?Discount => {
  const enabled = selectWelcomeOfferEnabled(state);
  const raw = selectRaw(state);

  if (!enabled) {
    return undefined;
  }

  return raw.codes.find(
    discount =>
      discount.code.match(/^WELCOME.*/) &&
      props.extension.toUpperCase() === discount.extension.toUpperCase() &&
      props.currency.toUpperCase() === discount.currency.toUpperCase(),
  );
};

export const selectHasWelcomeOffer = (
  state: FullState,
  extension: string,
): boolean => {
  const enabled = selectWelcomeOfferEnabled(state);
  const raw = selectRaw(state);

  if (!enabled) {
    return false;
  }

  return !!raw.codes.find(
    discount =>
      discount.code.match(/^WELCOME.*/) &&
      extension.toUpperCase() === discount.extension.toUpperCase(),
  );
};

export const selectWelcomeOfferPrice = (
  state: FullState,
  props: WelcomeOfferProps,
): number => {
  const enabled = selectWelcomeOfferEnabled(state);
  const raw = selectRaw(state);

  if (!enabled || !selectHasWelcomeOffer(state, props.extension)) {
    return 0;
  }

  return (
    raw.price_virtual_currencies[props.currency] ||
    selectWelcomeOffer(state, props)?.price_before_taxes ||
    0
  );
};

export const selectAreWelcomeOfferCodesInCart = (state: FullState): boolean => {
  const discounts = Object.keys(selectRawDiscounts(state) || {});
  return selectAllWelcomeOfferCode(state).some(discountCode =>
    discounts.includes(discountCode),
  );
};

export const selectExclusiveExtensionList = (
  state: FullState,
): Array<string> => {
  const raw = selectRaw(state);
  return Object.keys(raw.excludes || EMPTY_OBJECT);
};

export const selectTldListToExclude = (
  state: FullState,
  extension: string,
): Array<string> => {
  const raw = selectRaw(state);
  return (raw.excludes && raw.excludes[extension]) || EMPTY_ARRAY;
};

export const selectIsWelcomeOfferInBundle = (state: FullState): boolean =>
  selectBundledResults(state, DEFAULT_BUNDLE_TYPE).some(result =>
    selectHasWelcomeOffer(state, result.tld),
  );

export const selectIsUserSameAsReferer = (state: FullState): boolean => {
  const userId = selectPersonalId(state);
  const refererId = selectCartReferer(state);
  return userId === refererId;
};
